import { useRef, useState, type KeyboardEvent } from "react";
import { Paperclip, Send } from "lucide-react";

interface Props {
  onSend: (text: string) => void;
  isSending: boolean;
}

export function PostVerdictInput({ onSend, isSending }: Props) {
  const [text, setText] = useState("");
  const [focused, setFocused] = useState(false);
  const ref = useRef<HTMLTextAreaElement | null>(null);

  const handleSubmit = () => {
    if (!text.trim() || isSending) return;
    onSend(text);
    setText("");
    if (ref.current) ref.current.style.height = "auto";
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const autoResize = (el: HTMLTextAreaElement) => {
    el.style.height = "auto";
    const max = 4 * 22; // ~ 4 rows
    el.style.height = Math.min(el.scrollHeight, max) + "px";
  };

  return (
    <div
      data-testid="post-verdict-input"
      data-rp="input-fixed"
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 70,
        background: "var(--ivory)",
        borderTop: "0.5px solid var(--stone-mid)",
        padding: "16px 32px",
      }}
    >
      <div
        style={{
          maxWidth: 720,
          margin: "0 auto",
          display: "flex",
          alignItems: "flex-end",
          gap: 0,
          background: focused ? "var(--ivory)" : "var(--cream)",
          border: `0.5px solid ${focused ? "var(--accent-cramoisi)" : "var(--stone-mid)"}`,
          transition: "border-color .2s, background .2s",
        }}
      >
        <button
          type="button"
          aria-label="Joindre une pièce"
          style={{
            background: "none",
            border: "none",
            padding: 12,
            cursor: "pointer",
            color: "var(--smoke)",
            display: "flex",
            alignItems: "center",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--leather)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--smoke)")}
        >
          <Paperclip size={18} />
        </button>
        <textarea
          ref={ref}
          value={text}
          rows={1}
          onChange={(e) => {
            setText(e.target.value);
            autoResize(e.target);
          }}
          onKeyDown={onKey}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="Précisez votre question au porte-parole…"
          style={{
            flex: 1,
            background: "transparent",
            border: "none",
            outline: "none",
            resize: "none",
            padding: "14px 8px",
            fontFamily: '"Playfair Display", serif',
            fontStyle: "italic",
            fontSize: 14,
            color: "var(--charcoal)",
            lineHeight: 1.5,
            maxHeight: 4 * 22,
          }}
        />
        <button
          type="button"
          aria-label="Envoyer"
          onClick={handleSubmit}
          disabled={!text.trim() || isSending}
          style={{
            width: 44,
            height: 44,
            background: "var(--gold-deep)",
            color: "var(--ivory)",
            border: "none",
            cursor: text.trim() && !isSending ? "pointer" : "not-allowed",
            opacity: text.trim() && !isSending ? 1 : 0.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background .2s",
          }}
          onMouseEnter={(e) => {
            if (text.trim() && !isSending)
              e.currentTarget.style.background = "var(--accent-cramoisi)";
          }}
          onMouseLeave={(e) => (e.currentTarget.style.background = "var(--gold-deep)")}
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
}

export default PostVerdictInput;
